import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TableSortLabel,
    Paper,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    useTheme,
    Snackbar,
    Alert,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import useSWR from "swr";
import API_ENDPOINTS from "../../apis/endpoints";
import SearchInput from "../../components/SearchInput";
import TenmplatePopup from "./popups/TenmplatePopup";
import useAuth from "../../context";
import NewsLetterLanding from "./NewsLetterLanding";

const NewsletterList = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("updated_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [open, setOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [newsletterToDelete, setNewsletterToDelete] = useState(null);
    const auth = useAuth();
    const storedDeviceId = localStorage.getItem("device-id");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;

    const { data, isLoading, mutate } = useSWR(
        `${API_ENDPOINTS.NEWSLETTER}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    const newsletters = data?.results ?? [];
    const totalNewsletters = data?.meta?.total ?? 0;
    const summary = data?.summary ?? {};

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setPage(0);
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRowClick = (newsletter) => {
        if (newsletter.status === "SENT") {
            navigate(`/dashboard/newsletter/preview/${newsletter.id}`);
        } else {
            navigate(`/dashboard/newsletter/create?edit=${newsletter.id}`);
        }
    };

    const handleActionClick = (e, newsletterId) => {
        e.stopPropagation();
        setNewsletterToDelete(newsletterId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await fetch(`${API_ENDPOINTS.NEWSLETTER}${newsletterToDelete}/`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
            });
            mutate(); // Refresh the data
            setDeleteConfirmOpen(false);
            setNewsletterToDelete(null);
            setSnackbar({
                open: true,
                message: "Newsletter deleted successfully",
                severity: "success",
            });
        } catch (error) {
            console.error("Error deleting newsletter:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setNewsletterToDelete(null);
    };
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    if (isLoading) {
        return (
            <Box className="loader-container">
                <CircularProgress />
            </Box>
        );
    }

    if (!data?.results?.length) {
        return <NewsLetterLanding />;
    }

    return (
        <Box className="newsletter-list-container">
            <Typography variant="featureAccent" gutterBottom>
                Newsletters
            </Typography>
            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
                mt={3}
            >
                <SearchInput
                    onSearch={handleSearch}
                    placeHolder="News..."
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    sx={{ width: "71.7vw" }}
                />
                <Button
                    variant="contained"
                    onClick={() => setOpen(true)}
                    sx={{ marginLeft: "20px" }}
                >
                    create new
                </Button>
            </Box>
            {isLoading ? (
                <Box className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <Paper className="table-paper">
                    <TableContainer className="table-container">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {" "}
                                    {summary?.sortable_columns?.map(
                                        (column) =>
                                            column !== "attachments" && (
                                                <TableCell
                                                    key={column}
                                                    className="table-header-cell"
                                                >
                                                    {" "}
                                                    <TableSortLabel
                                                        active={
                                                            sortColumn ===
                                                            column
                                                        }
                                                        direction={
                                                            sortColumn ===
                                                            column
                                                                ? sortDirection
                                                                : "asc"
                                                        }
                                                        onClick={() =>
                                                            handleSortRequest(
                                                                column
                                                            )
                                                        }
                                                    >
                                                        {" "}
                                                        {column
                                                            .split("_")
                                                            .map(
                                                                (word) =>
                                                                    word
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase() +
                                                                    word.slice(
                                                                        1
                                                                    )
                                                            )
                                                            .join(" ")}{" "}
                                                    </TableSortLabel>{" "}
                                                </TableCell>
                                            )
                                    )}{" "}
                                    <TableCell className="table-header-cell">
                                        {" "}
                                        Actions{" "}
                                    </TableCell>{" "}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {" "}
                                {newsletters.length > 0 ? (
                                    newsletters.map((newsletter) => (
                                        <TableRow
                                            key={newsletter.id}
                                            className="table-row"
                                            onClick={() =>
                                                handleRowClick(newsletter)
                                            }
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(0, 0, 0, 0.04)",
                                                },
                                            }}
                                        >
                                            {" "}
                                            <TableCell>
                                                {" "}
                                                {new Date(
                                                    newsletter.updated_at
                                                ).toLocaleString()}{" "}
                                            </TableCell>{" "}
                                            <TableCell>
                                                {newsletter.name}
                                            </TableCell>{" "}
                                            <TableCell>
                                                {newsletter.subject}
                                            </TableCell>{" "}
                                            <TableCell>
                                                {newsletter.sender}
                                            </TableCell>{" "}
                                            <TableCell>
                                                {newsletter.recipients.join(
                                                    ", "
                                                )}
                                            </TableCell>{" "}
                                            <TableCell>
                                                {newsletter.status}
                                            </TableCell>{" "}
                                            <TableCell className="table-cell">
                                                {" "}
                                                <IconButton
                                                    onClick={(e) =>
                                                        handleActionClick(
                                                            e,
                                                            newsletter.id
                                                        )
                                                    }
                                                >
                                                    {" "}
                                                    <DeleteOutlineRoundedIcon
                                                        sx={{
                                                            color: theme.palette
                                                                .graph.magenta,
                                                        }}
                                                    />{" "}
                                                </IconButton>{" "}
                                            </TableCell>{" "}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        {" "}
                                        <TableCell colSpan={8} align="center">
                                            {" "}
                                            No newsletters found.{" "}
                                        </TableCell>{" "}
                                    </TableRow>
                                )}{" "}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalNewsletters}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}
            <TenmplatePopup open={open} onClose={() => setOpen(false)} />
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Deletion"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this newsletter?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default NewsletterList;
